import { useCombobox } from 'downshift';
import { useFormApi } from 'informed';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useState } from 'react';

import Input from './Input';

import '../styles/comboboxStyles.css';

const alwaysOpenTextStyle = 'py-1.5 px-3';
const textStyle =
  'absolute py-1.5 px-3 left-0 w-full bg-white rounded top-full shadow-dropdown z-dropdown';

const Combobox = ({
  disabled,
  dropdownHeaderText,
  field,
  hideDownButton,
  hideLabel,
  initialValue,
  isLoading,
  items,
  itemsAlwaysOpen,
  labelText,
  loadingText,
  maxVisible,
  noBorder,
  noResultsText,
  onBlur,
  onItemSelect,
  placeholder,
  toggleButtonLabelText,
  validate,
  validateOnBlur,
  validateOnChange,
  validateOnMount,
}) => {
  const [filteredItems, setFilteredItems] = useState(items);
  const initialItem = initialValue
    ? items.find((item) => item.value === initialValue)
    : null;
  const initialDisplayValue = initialItem
    ? initialItem.displayName
    : initialValue;
  const [userInput, setUserInput] = useState(initialDisplayValue);
  const formApi = useFormApi();

  /**
   * needed to handle items that arrive asynchronously, after initial mount/render and state is
   * set. list filtering is being handled in this useEffect now instead of using the downshift hook,
   * in order to correctly handle user input, and apply the filter, prior to items arriving
   */
  useEffect(() => {
    const input = userInput ? userInput.toLowerCase() : userInput;
    if (input) {
      setFilteredItems(
        items.filter(({ searchIndex }) => {
          return searchIndex.some((item) => item.toLowerCase().includes(input));
        }),
      );
    } else {
      // If there is no input, show all the items.
      setFilteredItems(items);
    }
  }, [items, userInput]);

  formApi.emitter.on('reset', () => {
    setUserInput(initialDisplayValue);
  });

  formApi.emitter.on('value', (fieldName, value) => {
    if (field === fieldName) {
      const itemChange = items.find((item) => item.value === value);
      setUserInput(itemChange ? itemChange.displayName : value);
    }
  });

  const {
    isOpen,
    getComboboxProps,
    getInputProps,
    getItemProps,
    getLabelProps,
    getMenuProps,
    getToggleButtonProps,
    highlightedIndex,
  } = useCombobox({
    items: filteredItems,
    itemToString: (item) => (item ? item.displayName : ''),
    onInputValueChange: ({ inputValue = '' }) => setUserInput(inputValue),
    onSelectedItemChange: (item) => {
      const { selectedItem } = item;
      // This can't be bound on initial mount, as the child component doesn't exist yet.
      const { fieldApi } = formApi.getField(field);
      fieldApi.setValue(selectedItem?.value);
      onItemSelect(selectedItem);
    },
  });

  const showNoResultsText =
    noResultsText.length > 0 && filteredItems.length === 0;
  const hasDropdownHeader = dropdownHeaderText.length > 0 && items.length > 0;
  const remCalculation = hasDropdownHeader
    ? maxVisible * 2.25 + 2.25 // an extra 2.25rem to account for the dropdown header
    : maxVisible * 2.25;

  const itemsHeader = hasDropdownHeader ? (
    <li className="m-0 pt-3 pb-1 px-3 font-semibold text-sm uppercase text-gray-4">
      {dropdownHeaderText}
    </li>
  ) : null;

  const itemsList = filteredItems.map((item, index) => {
    return (
      <li
        className={`m-0 ${
          item.isUnprivileged
            ? 'unprivileged-item-combobox'
            : `py-1.5 px-3 ${
                index === highlightedIndex && 'bg-cyan-1 text-blue-4'
              }`
        }`}
        // eslint-disable-next-line react/no-array-index-key
        key={`${item.id}-${index}`}
        {...getItemProps({ index })}
      >
        {item.content}
      </li>
    );
  });

  const itemsDisplay = itemsAlwaysOpen ? (
    <Fragment>
      <ul
        {...getMenuProps({
          className: 'pl-0 m-0 overflow-y-auto list-none',
        })}
        style={{ maxHeight: `${remCalculation}rem` }}
      >
        {itemsHeader}
        {!isLoading && itemsList}
      </ul>
      {isLoading && <div className={alwaysOpenTextStyle}>{loadingText}</div>}
      {!isLoading && showNoResultsText && (
        <div className={alwaysOpenTextStyle}>{noResultsText}</div>
      )}
    </Fragment>
  ) : (
    <Fragment>
      <ul
        {...getMenuProps({
          className:
            'absolute left-0 w-full pl-0 m-0 overflow-y-auto list-none bg-white rounded top-full shadow-dropdown z-dropdown',
        })}
        style={{ maxHeight: `${remCalculation}rem` }}
      >
        {isOpen && itemsHeader}
        {!isLoading && isOpen && itemsList}
      </ul>

      {isLoading && isOpen && <div className={textStyle}>{loadingText}</div>}

      {!isLoading && isOpen && showNoResultsText && (
        <div className={textStyle}>{noResultsText}</div>
      )}
    </Fragment>
  );

  return (
    <div className="relative">
      {/* NOTE: downshift handles the label/control with `getLabelProps` */}
      {/* eslint-disable-next-line jsx-a11y/label-has-for */}
      <label
        {...getLabelProps({ hidden: hideLabel, 'aria-hidden': hideLabel })}
      >
        {labelText}
      </label>
      <div {...getComboboxProps()} className="relative combobox">
        <Input
          {...getInputProps({
            disabled,
            field,
            initialValue,
            placeholder,
            refKey: 'inputRef',
            userInput,
            validate,
            validateOnBlur,
            validateOnChange,
            validateOnMount,
          })}
          getToggleButtonProps={getToggleButtonProps}
          hideDownButton={itemsAlwaysOpen || hideDownButton}
          noBorder={noBorder}
          onBlur={onBlur}
          toggleButtonLabelText={toggleButtonLabelText}
        />
      </div>

      {itemsDisplay}
    </div>
  );
};

export const ComboboxItemsProptypes = PropTypes.arrayOf(
  PropTypes.shape({
    // content - this is what gets displayed in the dropdown
    content: PropTypes.node.isRequired,
    // displayName - this is what gets displayed in the input when an item is selected
    displayName: PropTypes.string.isRequired,
    // isUnprivileged - this is what determines if the item is unprivileged workspace
    isUnprivileged: PropTypes.bool,
    // searchIndex - this is what is being searched as the user types in the input
    searchIndex: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    // value - this is what we care about, and what we set in the Informed form, for use with a service etc
    value: PropTypes.string.isRequired,
  }),
);

Combobox.defaultProps = {
  disabled: false,
  dropdownHeaderText: '',
  hideDownButton: false,
  hideLabel: false,
  initialValue: '',
  isLoading: false,
  itemsAlwaysOpen: false,
  loadingText: '',
  maxVisible: 4,
  noBorder: false,
  noResultsText: '',
  onBlur: () => {},
  onItemSelect: () => {},
  placeholder: '',
  toggleButtonLabelText: undefined,
  validate: () => {},
  validateOnBlur: false,
  validateOnChange: false,
  validateOnMount: false,
};

Combobox.propTypes = {
  disabled: PropTypes.bool,
  dropdownHeaderText: PropTypes.string,
  field: PropTypes.string.isRequired,
  hideDownButton: PropTypes.bool,
  hideLabel: PropTypes.bool,
  initialValue: PropTypes.string,
  isLoading: PropTypes.bool,
  items: ComboboxItemsProptypes.isRequired,
  itemsAlwaysOpen: PropTypes.bool,
  labelText: PropTypes.string.isRequired,
  loadingText: PropTypes.string,
  maxVisible: PropTypes.number,
  noBorder: PropTypes.bool,
  noResultsText: PropTypes.string,
  onBlur: PropTypes.func,
  onItemSelect: PropTypes.func,
  placeholder: PropTypes.string,
  toggleButtonLabelText: PropTypes.string,
  validate: PropTypes.func,
  validateOnBlur: PropTypes.bool,
  validateOnChange: PropTypes.bool,
  validateOnMount: PropTypes.bool,
};

export default Combobox;
